import { useEffect, useState } from 'react';
import { useLoading } from '../../../../../../hooks/useLoading';
import {
  loadEmailSetupData,
  determineStartingStep,
  ICharityEmailSetup
} from '../../../../../../utils/firebase/emailSetup';
import { fetchSendingEmails, SendingEmail } from '../../../../../../utils/fetch';

interface UseEmailSetupDataProps {
  charityId: string | undefined;
  adminUrl?: string;
  serviceAccessToken?: string;
}

interface UseEmailSetupDataReturn {
  loading: boolean;
  startingStep: number;
  emailSetupData: ICharityEmailSetup | null;
  existingSendingEmails: SendingEmail[];
  error: string | null;
}

const MINIMUM_LOADING_DURATION_MS = 800;

export function useEmailSetupData({
  charityId,
  adminUrl,
  serviceAccessToken,
}: UseEmailSetupDataProps): UseEmailSetupDataReturn {
  const { loading, runWithLoading, setLoading } = useLoading(true);
  const [startingStep, setStartingStep] = useState(0);
  const [emailSetupData, setEmailSetupData] = useState<ICharityEmailSetup | null>(null);
  const [existingSendingEmails, setExistingSendingEmails] = useState<SendingEmail[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!charityId) {
      setLoading(false);
      setEmailSetupData(null);
      setExistingSendingEmails([]);
      setStartingStep(0);
      setError(null);
      return;
    }

    runWithLoading(async () => {
      try {
        setError(null);
        const data = await loadEmailSetupData(charityId);
        setEmailSetupData(data);

        const { step } = determineStartingStep(data);
        setStartingStep(step);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load email setup data';
        console.error('Failed to load email setup data:', err);
        setError(errorMessage);
        setStartingStep(0);
      }
    }, MINIMUM_LOADING_DURATION_MS);
  }, [charityId, runWithLoading, setLoading]);

  useEffect(() => {
    if (!charityId || !adminUrl || !serviceAccessToken) {
      return;
    }
    if (!emailSetupData?.sendingEmailsConfigured) {
      return;
    }

    let cancelled = false;

    fetchSendingEmails(adminUrl, charityId, serviceAccessToken)
      .then(emails => {
        if (!cancelled) {
          setExistingSendingEmails(emails);
        }
      })
      .catch(fetchErr => {
        console.warn('Failed to fetch existing sending emails:', fetchErr);
      });

    return () => {
      cancelled = true;
    };
  }, [charityId, adminUrl, serviceAccessToken, emailSetupData?.sendingEmailsConfigured]);

  return {
    loading,
    startingStep,
    emailSetupData,
    existingSendingEmails,
    error,
  };
}
